import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, ShieldCheck } from 'lucide-react';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '../components/ui/input-otp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (code.length !== 6) {
      toast.error('Por favor ingresa el código completo');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        code,
        new_password: newPassword
      });
      toast.success('¡Contraseña actualizada! Ahora puedes iniciar sesión.');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al restablecer contraseña';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#F8FAFC] p-4">
      <div className="w-full max-w-md bg-background border shadow-[0_12px_32px_rgba(0,0,0,0.08)] p-8 rounded-sm relative overflow-hidden">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center">
            <ShieldCheck className="h-8 w-8 text-accent" />
          </div>
        </div>

        {/* Header */}
        <div className="text-center space-y-2 mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-primary">
            Restablecer Contraseña
          </h1>
          <p className="text-base text-muted-foreground">
            Ingresa el código que recibiste en
          </p>
          <p className="text-sm font-medium text-primary" data-testid="reset-email-display">
            {email}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* OTP Input */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Código de Recuperación</Label>
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(value) => setCode(value)}
                data-testid="reset-otp-input"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="h-12 w-12 text-lg rounded-sm" />
                  <InputOTPSlot index={1} className="h-12 w-12 text-lg rounded-sm" />
                  <InputOTPSlot index={2} className="h-12 w-12 text-lg rounded-sm" />
                  <InputOTPSlot index={3} className="h-12 w-12 text-lg rounded-sm" />
                  <InputOTPSlot index={4} className="h-12 w-12 text-lg rounded-sm" />
                  <InputOTPSlot index={5} className="h-12 w-12 text-lg rounded-sm" />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label htmlFor="new_password" className="text-sm font-medium">
              Nueva Contraseña
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                id="new_password"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pl-10 h-11 rounded-sm"
                required
                minLength={6}
                data-testid="reset-password-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">Mínimo 6 caracteres</p>
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirm_password" className="text-sm font-medium">
              Confirmar Contraseña
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                id="confirm_password"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 h-11 rounded-sm"
                required
                minLength={6}
                data-testid="reset-confirm-password-input"
              />
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium transition-all active:scale-95"
            data-testid="reset-submit-button"
          >
            {loading ? 'Actualizando...' : 'Restablecer contraseña'}
          </Button>
        </form>

        {/* Info */}
        <div className="mt-6 p-4 bg-secondary/50 rounded-sm border border-border">
          <p className="text-xs text-muted-foreground text-center">
            El código expira en 15 minutos
          </p>
        </div>
      </div>
    </div>
  );
}
